import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, Mail, TrendingUp, Clock, CheckCircle, XCircle, Shield, Crown, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminTeam() {
  const { token, user } = useAuth();
  const [teamData, setTeamData] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ totalSales: 0, totalLeads: 0, totalAdmins: 0 });

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/team-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamData(response.data.team || []);
      setAdmins(response.data.admins || []);
      setTotals({
        totalSales: response.data.total_sales_users || 0,
        totalLeads: response.data.total_leads_assigned || 0,
        totalAdmins: response.data.total_admins || 0
      });
    } catch (error) {
      console.error('Errore nel caricamento dati team:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (email, name) => {
    if (!window.confirm(`Sei sicuro di voler eliminare ${name} (${email})?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/admin/users/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${name} eliminato con successo`);
      fetchTeamData(); // Ricarica la lista
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Gestione Team</h1>
        <p className="text-slate-600">Visualizza admin e commerciali registrati</p>
      </div>

      {/* Statistiche generali */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Admin</p>
              <p className="text-4xl font-bold mt-1">{totals.totalAdmins}</p>
            </div>
            <Crown className="h-12 w-12 text-purple-200" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm font-medium">Commerciali</p>
              <p className="text-4xl font-bold mt-1">{totals.totalSales}</p>
            </div>
            <Users className="h-12 w-12 text-indigo-200" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">Lead Totali</p>
              <p className="text-4xl font-bold mt-1">{totals.totalLeads}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-emerald-200" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm font-medium">Media Lead/Commerciale</p>
              <p className="text-4xl font-bold mt-1">
                {totals.totalSales > 0 ? Math.round(totals.totalLeads / totals.totalSales) : 0}
              </p>
            </div>
            <Clock className="h-12 w-12 text-amber-200" />
          </div>
        </Card>
      </div>

      {/* Sezione Admin */}
      <Card className="overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-purple-200 bg-purple-50">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg font-bold text-purple-900">Amministratori</h2>
          </div>
        </div>
        
        {admins.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Shield className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>Nessun amministratore trovato</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-purple-50 border-b border-purple-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-purple-600 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-purple-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-purple-600 uppercase tracking-wider">
                    Ruolo
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-purple-600 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-purple-100">
                {admins.map((admin, index) => (
                  <tr key={index} className="hover:bg-purple-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Crown className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="font-medium text-slate-900">{admin.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4" />
                        <span>{admin.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                        <Crown className="h-3 w-3 mr-1" />
                        Amministratore
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {admin.email !== user?.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(admin.email, admin.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Sezione Commerciali */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-indigo-200 bg-indigo-50">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-indigo-900">Commerciali</h2>
          </div>
        </div>
        
        {teamData.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>Nessun commerciale registrato</p>
            <p className="text-sm mt-1">I nuovi utenti che si registrano diventeranno commerciali automaticamente</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-indigo-50 border-b border-indigo-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                    Commerciale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                    Lead Totali
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                    In Lavorazione
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                    Convertiti
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                    Persi
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-indigo-100">
                {teamData.map((member, index) => (
                  <tr key={index} className="hover:bg-indigo-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-600 font-semibold">
                            {member.name?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                        <span className="font-medium text-slate-900">{member.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4" />
                        <span>{member.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700">
                        {member.total_leads}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                        <Clock className="h-3 w-3 mr-1" />
                        {member.in_progress}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {member.converted}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                        <XCircle className="h-3 w-3 mr-1" />
                        {member.lost}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(member.email, member.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
